import { MoreVertical, Pencil, Trash2, ShieldOff } from 'lucide-react';
import { useRef, useState } from 'react';

import { Badge } from './Badge';
import { ServiceIcon } from './ServiceIcon';
import { formatRelativeTime } from '../lib/format-relative-time';
import { getCredentialType } from '../lib/credential-types';

import type { CredentialIsolation, CredentialStatus } from '../lib/types';

export interface CredentialListItemProps {
  id: string;
  name: string | null;
  serviceName: string;
  credentialType: string | null;
  status: CredentialStatus;
  isolation?: CredentialIsolation;
  createdAt: string;
  isEnabledForTenant?: boolean;
  showEnableToggle?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  onRevoke?: () => void;
  onDelete?: () => void;
  onToggleEnable?: () => void;
  className?: string;
}

const STATUS_BADGE: Record<CredentialStatus, { variant: 'success' | 'warning' | 'error' | 'info'; label: string; tooltip: string }> = {
  active: { variant: 'success', label: 'Active', tooltip: 'Ready to use — approved and available for agent execution' },
  pending: { variant: 'warning', label: 'Pending', tooltip: 'Awaiting approval — requested but not yet granted' },
  expired: { variant: 'error', label: 'Expired', tooltip: 'Time-limited access ended — validity period has passed' },
  revoked: { variant: 'error', label: 'Revoked', tooltip: 'Access removed — manually revoked by a user' },
};

const STATUS_BORDER: Record<CredentialStatus, string> = {
  active: 'border-sd-success',
  pending: 'border-sd-warning',
  expired: 'border-sd-error',
  revoked: 'border-sd-error/50',
};

export function CredentialListItem({
  name,
  serviceName,
  credentialType,
  status,
  isolation,
  createdAt,
  isEnabledForTenant,
  showEnableToggle,
  onClick,
  onEdit,
  onRevoke,
  onDelete,
  onToggleEnable,
  className = '',
}: CredentialListItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const typeDef = credentialType ? getCredentialType(credentialType) : undefined;
  const displayName = name ?? serviceName;
  const displayType = typeDef?.label ?? serviceName;
  const badge = STATUS_BADGE[status];
  const isRevoked = status === 'revoked' || status === 'expired';

  return (
    <div
      role="listitem"
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      className={`flex items-center gap-sd-2 min-h-[56px] px-sd-3 py-sd-2 bg-sd-surface border-b border-sd-border border-l-2 ${STATUS_BORDER[status]} transition-all duration-150 ease-in motion-reduce:transition-none ${onClick ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 hover:bg-sd-surface-elevated/60' : ''} ${isRevoked ? 'opacity-60' : ''} ${className}`.trim()}
    >
      {/* Icon circle */}
      <ServiceIcon
        slug={credentialType ?? ''}
        label={displayType}
        brandColor={typeDef?.brandColor ?? '#6b7280'}
        size={32}
      />

      {/* Name + type */}
      <div className="flex flex-col min-w-0 flex-1">
        <span className={`text-sd-base font-medium text-sd-text truncate ${isRevoked ? 'line-through' : ''}`}>{displayName}</span>
        <span className="text-sd-sm text-sd-text-secondary truncate">{displayType}</span>
      </div>

      {/* Isolation badge */}
      {isolation === 'system' && (
        <Badge variant="info" size="sm">System</Badge>
      )}
      {isolation === 'tenant' && (
        <Badge variant="info" size="sm">Team</Badge>
      )}

      {/* Enable/disable toggle for system credentials */}
      {showEnableToggle && onToggleEnable && (
        <button
          aria-label={isEnabledForTenant ? 'Disable system credential' : 'Enable system credential'}
          className={`relative inline-flex h-5 w-9 items-center rounded-full shrink-0 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary ${isEnabledForTenant ? 'bg-sd-success' : 'bg-sd-border/50'}`}
          onClick={(e) => { e.stopPropagation(); onToggleEnable(); }}
        >
          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${isEnabledForTenant ? 'translate-x-4' : 'translate-x-1'}`} />
        </button>
      )}

      {/* Status badge with tooltip */}
      <div className="relative group/tooltip shrink-0">
        <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
        <div
          role="tooltip"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-sd-2 py-sd-1 rounded-sd-sm bg-sd-surface-overlay text-sd-text text-sd-xs whitespace-nowrap opacity-0 pointer-events-none group-hover/tooltip:opacity-100 transition-opacity duration-150 shadow-sd-md z-40"
        >
          {badge.tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-sd-surface-overlay" />
        </div>
      </div>

      {/* Timestamp */}
      <span className="text-sd-xs text-sd-text-secondary shrink-0 hidden @md:inline">{formatRelativeTime(createdAt)}</span>

      {/* Kebab menu */}
      {(onEdit || onRevoke || onDelete) && (
        <div className="relative shrink-0" ref={menuRef}>
          <button
            aria-label="Credential actions"
            className="p-sd-1 rounded-sd-sm text-sd-text-secondary hover:bg-sd-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary transition-colors duration-150"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
          >
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-sd-1 w-40 bg-sd-surface-elevated border border-sd-border/30 rounded-sd-md shadow-sd-md z-30 py-sd-1">
              {onEdit && (
                <button
                  className="flex items-center gap-sd-2 w-full px-sd-3 py-sd-2 text-sd-sm text-sd-text hover:bg-sd-surface transition-colors"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit(); }}
                >
                  <Pencil size={14} /> Edit
                </button>
              )}
              {onRevoke && status === 'active' && (
                <button
                  className="flex items-center gap-sd-2 w-full px-sd-3 py-sd-2 text-sd-sm text-sd-warning hover:bg-sd-surface transition-colors"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onRevoke(); }}
                >
                  <ShieldOff size={14} /> Revoke
                </button>
              )}
              {onDelete && !isRevoked && (
                <button
                  className="flex items-center gap-sd-2 w-full px-sd-3 py-sd-2 text-sd-sm text-sd-error hover:bg-sd-surface transition-colors"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(); }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
